<template>
	<div class="ln-app ln-app--simulator s-app">
		<progress v-bind:value="currentStep / steps.length" class="s-app__progress"></progress>
		<Form
			v-bind:steps="steps"
			v-bind:currentStep="currentStep"
			v-bind:i18n="i18n"
			v-bind:locale="locale"
			v-if="currentStep < steps.length"
		/>
		<Summary
			v-bind:summary="summary"
			v-bind:data="data"
			v-bind:i18n="i18n"
			v-bind:locale="locale"
			v-bind:isPublic="isPublic"
			v-bind:getSignupUrl="getSignupUrl"
			v-bind:getLoginUrl="getLoginUrl"
			v-bind:getDashboardUrl="getDashboardUrl"
			v-bind:getPrintUrl="getPrintUrl"
			v-if="currentStep === steps.length"
		/>
		<div class="s-app__nav">
			<router-link
				v-bind:to="`/step/${currentStep - 1}`"
				v-show="currentStep > 0"
				tag="button"
				class="mdc-button mdc-button--outlined"
				>{{ i18n.prev[locale] }}</router-link
			>
			<router-link
				v-bind:to="`/step/${currentStep + 1}`"
				v-show="currentStep < steps.length"
				class="mdc-button mdc-button--raised s-app__nav-next"
				tag="button"
				:data-event="`step-${currentStep + 1}`"
				>{{ i18n.next[locale] }}</router-link
			>
		</div>
		<Graphs v-bind:graphs="graphs" v-bind:i18n="i18n" v-bind:locale="locale" />
		<div class="s-app__actions" v-show="Object.keys(this.data).length">
			<button type="button" v-on:click="reset" class="mdc-button">
				{{ i18n.reset[locale] }}
			</button>
			<button
				type="button"
				v-on:click="showSummary"
				v-show="currentStep < steps.length"
				class="mdc-button s-app__actions-next"
			>
				{{ i18n.showSummary[locale] }}
			</button>
		</div>
		<div class="s-app__disclaimer">
			<p class="s-app__disclaimer mdc-typography" v-html="i18n.disclaimer[locale]" />
		</div>
	</div>
</template>

<script>
import 'whatwg-fetch';
import config from '../config';
import actionHelpers from './helpers/actions';
import Form from './components/Form.vue';
import Graphs from './components/Graphs.vue';
import Summary from './components/Summary.vue';

export default {
	name: 'app',
	components: {
		Form,
		Graphs,
		Summary
	},
	props: {
		localeSetting: {
			default: 'de'
		},
		isPublicSetting: {
			default: false
		},
		initialData: {
			default: () => null
		},
		domainSetting: {
			type: String,
			default: config.domain
		}
	},
	data(props) {
		const { localeSetting, isPublicSetting, domainSetting } = props;
		const { summary, i18n, steps } = config;

		const graphs = config.graphs.map(graph => {
			graph.disabled = graph.isDisabled ? graph.isDisabled({}) : false;

			return graph;
		});

		return {
			data: {},
			graphs,
			steps,
			summary,
			i18n,
			locale: localeSetting,
			isPublic: isPublicSetting,
			domain: domainSetting,
			localStorageKey: config.localStorageKey
		};
	},
	computed: {
		currentStep() {
			const { id } = this.$route.params;

			return id ? parseInt(id, 10) : 0;
		}
	},
	watch: {
		steps: {
			handler(steps) {
				// Format selection
				this.data = steps.reduce((acc, step) => {
					const { name, value, options } = step;

					if (value && value.length) {
						acc[name] = value;
					}

					options.forEach(option => {
						const { sub } = option;

						if (sub) {
							const { name, value } = sub;

							if (value && value.length) {
								acc[name] = value;
							}
						}
					});

					return acc;
				}, {});

				// Calculate graphs
				this.graphs = this.graphs.map(graph => {
					graph.value = graph.calculateValue(this.data);
					graph.disabled = graph.isDisabled ? graph.isDisabled(this.data) : false;

					return graph;
				});

				// Persist
				if (Object.keys(this.data).length) {
					this.persistData({
						data: this.data,
						summary: this.getSummary()
					});
				}
			},
			deep: true
		}
	},
	mounted() {
		// Set up initial data
		const data = this.getInitialData();

		if (Object.keys(data).length) {
			this.steps = this.steps.map(step => {
				const { name, options } = step;

				step.value = data[name] || step.value;

				options.forEach(option => {
					const { sub } = option;

					if (sub) {
						const { name, value } = sub;

						sub.value = data[name] || value;
					}
				});

				return step;
			});

			this.graphs = this.graphs.map(graph => {
				graph.value = graph.calculateValue(data);
				graph.disabled = graph.isDisabled ? graph.isDisabled(data) : false;

				return graph;
			});
		}

		// Handle navigation
		document.addEventListener('keydown', event => {
			let index = null;

			switch (event.which) {
				case 37:
					index = Math.max(0, this.currentStep - 1);
					break;
				case 39:
					index = Math.min(this.steps.length, this.currentStep + 1);
					break;
			}

			if (index !== null && index !== this.currentStep) {
				this.$router.push(`/step/${index}`);
			}
		});
	},
	methods: {
		getSummary() {
			const ids = this.summary
				.filter(item => item.isChecked(this.data))
				.reduce((acc, item) => acc.concat(item.ids), []);

			return ids;
		},
		persistData(options = { summary: [], data: {} }) {
			let { summary, data } = options;

			localStorage.setItem(this.localStorageKey, JSON.stringify(data));

			this.persistDataRemote({
				summary,
				data
			});
		},
		async persistDataRemote(options = { summary: [], data: {} }) {
			if (this.isPublic) {
				return;
			}

			await fetch('#', {
				method: 'POST',
				credentials: 'same-origin',
				body: JSON.stringify(options),
				headers: {
					'Content-Type': 'application/json',
					'X-Requested-With': 'XMLHttpRequest'
				}
			});
		},
		getPersistedData() {
			const data = localStorage.getItem(this.localStorageKey);

			return data ? JSON.parse(data) : null;
		},
		getInitialData() {
			const data = this.initialData || this.getPersistedData() || {};

			return data;
		},
		async reset() {
			localStorage.removeItem(this.localStorageKey);

			await this.persistDataRemote();

			if (this.$router.currentRoute.path !== '/') {
				this.$router.push(`/`);
			}

			window.location.reload();
		},
		showSummary() {
			this.$router.push(`/step/${this.steps.length}`);
		},
		getSignupUrl() {
			const url = actionHelpers.getSignupUrl({
				data: this.data,
				summary: this.getSummary(),
				domain: this.domain,
				locale: this.locale
			});

			return url;
		},
		getLoginUrl() {
			const url = actionHelpers.getLoginUrl({
				data: this.data,
				summary: this.getSummary(),
				domain: this.domain,
				locale: this.locale
			});

			return url;
		},
		getPrintUrl() {
			const url = actionHelpers.getPrintUrl({
				data: this.data,
				summary: this.getSummary(),
				domain: this.domain,
				locale: this.locale
			});

			return url;
		},
		getDashboardUrl() {
			const url = this.domain;

			return url;
		}
	}
};
</script>

<style lang="scss">
.s-app {
	@import '@legacynotes/frontend/src/assets/css/variables-new';

	&__intro {
		margin-bottom: 4rem;
	}

	&__progress {
		appearance: none;
		width: 100%;
		height: 0.625rem;
		margin-bottom: 2rem;

		&::-webkit-progress-bar {
			background-color: rgba(#6d6d6e, 0.24);
		}
		&::-webkit-progress-value {
			background-color: #6d6d6e;
		}
	}

	&__nav,
	&__actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		margin-top: 2.5rem;

		&-next.mdc-button {
			margin-left: auto;
		}

		@media print {
			display: none;
		}
	}

	&__actions {
		margin-top: 3rem;
		opacity: 0.4;
		transition: opacity 0.3s ease-in-out;

		&:hover,
		&:focus-within {
			opacity: 1;
		}
	}

	&__disclaimer {
		margin-top: 3rem;
	}
}
</style>
